import { ResponseRecord } from 'types/Apis/SearchResponse';
import numeral from 'numeral';
import compact from 'lodash/compact';
import { colors, screen } from 'components/styles/variables';
import { useTranslation } from 'react-i18next';
import FavoriteIcon from 'components/FavoriteIcon';
import { LargeButton } from 'components/Shared/Buttons';
import css from 'styled-jsx/css';
import classNames from 'classnames';
import Spacer from 'components/Shared/Spacer';
import useFavorite from 'lib/hooks/useFavorite';
import { useMedia } from 'react-use';
import noImages from 'lib/Presenters/VdpPresenter/no-images.png';
import { SMALL } from 'lib/DeviceScreen';

import QuickPicksEventContext from 'contexts/QuickPicksEventContext';
import quickPicksEvent, {
  UserExperience,
} from 'lib/Tracking/Vdp/ListingImpressions/QuickPicksEvent';
import useEvents from 'lib/hooks/useEvents';
import { useEffect, useCallback, useState, useContext } from 'react';
import Carousel from 'nuka-carousel';
import Dots from 'components/Vdp/PhotoCarousel/Dots';
import { Arrow, Directions } from 'components/Shared/Arrow';
import Counter from 'components/Shared/Counter';
import { useSelector } from 'react-redux';
import State from 'types/State';
import LoadingImage from './LoadingImage';
import DetailCell from './DetailCell';
import styles from './styles';

interface Props {
  vehicle: ResponseRecord;
  skip: () => void;
  onContact: () => void;
  onFavorite: () => void;
  isSubmitting: boolean;
  hasSubmitted: boolean;
  index: number;
}

const useContactButtonText = (isContacting: boolean, isContacted: boolean): string => {
  const { t } = useTranslation();
  const isMobile = useMedia(`(max-width: ${SMALL.cssMaxWidth})`);

  if (isContacting) {
    return '';
  }

  const contactText = isMobile ? t('quickPicks.oneTapContact') : t('quickPicks.oneClickContact');

  return isContacted ? t('contacted') : contactText;
};

const buttonCss = css.resolve`
  flex-grow: 1;
  flex-basis: 0;
  height: 44px;
  font-size: 16px;
  padding: 9px 0px 11px 0px;

  @media (min-width: ${screen.smMin}) {
    height: 56px;
    font-size: 18px;
    padding: 13px 0px 16px 0px;
  }
`;

const skipButtonCss = css.resolve`
  @media (hover) {
    .skip:hover {
      transition: all 0.5s ease-in-out;
      background-color: ${colors.ALR1};
      color: white;
      border: solid 2px ${colors.ALR1};
    }
  }

  .skip {
    transition: all 0.5s ease-in-out;
    background-color: white;
    border: solid 2px ${colors.ALG1};
    color: ${colors.ALR1};
  }
`;

const contactButtonCss = css.resolve`
  .contact {
    transition: all 0.5s ease-in-out;
    background-color: ${colors.ALR1};
    color: white;
    border: solid 2px ${colors.ALR1};
    position: relative;
  }

  @media (hover) {
    .contact:hover {
      transition: all 0.5s ease-in-out;
      background-color: ${colors.ALR2};
      border-color: ${colors.ALR2};
    }
  }
`;

const heartCss = css.resolve`
  position: absolute;
  right: 13px;
  top: 13px;

  @media (min-width: ${screen.smMin}) {
    right: 18px;
    top: 18px;
  }
`;

const sliderCss = css.resolve`
  .image-slide {
    object-fit: cover;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }

  @media (min-width: ${screen.mdMin}) {
    .slider :global(.slider-control-bottomcenter) {
      display: none;
    }
  }
`;

const counterCss = css.resolve`
  div.counter {
    display: none;
    position: absolute;
    bottom: 16px;
    right: 16px;
  }

  @media (min-width: ${screen.mdMin}) {
    div.counter {
      display: flex;
    }
  }
`;

const dostsCss = css.resolve`
  ol.dots {
    width: 100%;
  }
`;

export default function VehicleCard({
  vehicle,
  skip,
  onContact,
  onFavorite,
  isSubmitting,
  hasSubmitted,
  index,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const isSignedIn = useSelector<State>((state) => state?.session?.isSignedIn);
  const { isFavorited, favorite } = useFavorite(vehicle.vin);

  const contactButtonText = useContactButtonText(isSubmitting, hasSubmitted);

  const quickPicksEventContext = useContext(QuickPicksEventContext);
  const logger = useEvents();
  const logQuickPicksEvent = useCallback(() => {
    logger.log(
      quickPicksEvent({
        vin: vehicle.vin,
        trackingParams: vehicle.tracking_params,
        vdpUserExperience: vehicle.experience as UserExperience,
        horizontalPosition: index,
        impressionType: 'view',
        sourcePage: quickPicksEventContext.sourcePage,
      })
    );
  }, [vehicle, logger, index, quickPicksEventContext.sourcePage]);

  useEffect(() => {
    logQuickPicksEvent();
  }, [logQuickPicksEvent]);

  const [slideIndex, setSlideIndex] = useState<number>(0);

  const hasPhotos = !!vehicle.photo_urls?.length;

  const MAX_LENGTH = 31;
  const dealerName =
    vehicle.dealer_name.length > MAX_LENGTH
      ? `${vehicle.dealer_name.substring(0, MAX_LENGTH)}...`
      : vehicle.dealer_name;

  return (
    <div className="vehicle-card">
      <div className="quick-picks-header">
        <div className="quick-picks-title">{t('vdp.buyersViewed')}</div>
      </div>
      <div className="loading-image-container">
        <LoadingImage />
        <figure className="quick-picks-image-container">
          <>
            {hasPhotos && (
              // we need the heightMode prop from <v5 nuka-carousel to calculate the height of on page slideshow https://github.com/autolist/autolist-web/pull/4471
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <Carousel
                slideIndex={slideIndex}
                afterSlide={(newIndex): void => {
                  setSlideIndex(newIndex);
                }}
                className={sliderCss.className}
                enableKeyboardControls={true}
                wrapAround={false}
                renderBottomCenterControls={({ slideCount, currentSlide }): JSX.Element => {
                  return (
                    <Dots
                      className={classNames(dostsCss.className)}
                      count={slideCount}
                      currentSlide={currentSlide}
                    />
                  );
                }}
                renderCenterLeftControls={({ previousSlide }): JSX.Element => (
                  <Arrow
                    direction={Directions.Left}
                    onClick={(): void => {
                      previousSlide();
                    }}
                    style={{ marginLeft: '16px' }}
                    alt={t('previous')}
                    data-testid="previous-slide-arrow"
                  />
                )}
                renderCenterRightControls={({ nextSlide }): JSX.Element => (
                  <Arrow
                    alt={t('next')}
                    onClick={(): void => {
                      nextSlide();
                    }}
                    style={{ marginRight: '16px' }}
                    direction={Directions.Right}
                  />
                )}
              >
                {vehicle.photo_urls.map((image) => (
                  <img
                    loading="lazy"
                    className="image-slide"
                    data-testid="image"
                    alt="qp-image"
                    src={image}
                    key={image}
                  />
                ))}
              </Carousel>
            )}

            {!hasPhotos && (
              <img
                src={noImages}
                className="quick-picks-image"
                data-testid="image"
                alt="qp-image"
              />
            )}

            {isSignedIn && (
              <FavoriteIcon
                data-testid="favorite"
                onClick={(): void => {
                  favorite();
                  setTimeout(() => onFavorite(), 1000);
                }}
                active={isFavorited}
                className={classNames(heartCss.className)}
              />
            )}

            {hasPhotos && (
              <Counter
                className={classNames('counter', counterCss.className)}
                count={vehicle.photo_urls.length}
                index={slideIndex}
              />
            )}
          </>
        </figure>
      </div>
      <div className="quick-picks-vehicle-details">
        <h2>{compact([vehicle.year, vehicle.make, vehicle.model, vehicle.trim]).join(' ')}</h2>
        <DetailCell
          topInfo={numeral(vehicle.price).format('$0,0')}
          bottomInfo={t('quickPicks.mileage', {
            miles: numeral(vehicle.mileage).format('0,0'),
          })}
          isRightCell={true}
          dataTestId="right"
        />
      </div>
      <div className="snapshot">
        <div className="dealer-name">{dealerName}</div>
        <div className="location">
          {vehicle.city}, {vehicle.state}
        </div>
      </div>
      <div className="quick-picks-actions">
        <LargeButton
          onClick={(): void => {
            setSlideIndex(0);
            skip();
          }}
          isGray={true}
          className={classNames('skip', skipButtonCss.className, buttonCss.className)}
        >
          {t('quickPicks.skip')}
        </LargeButton>
        <Spacer size="16px" smSize="24px" />
        <LargeButton
          onClick={(): void => {
            setSlideIndex(0);
            onContact();
          }}
          className={classNames('contact', contactButtonCss.className, buttonCss.className)}
        >
          {contactButtonText}
        </LargeButton>
      </div>
      <style jsx>{styles}</style>
      {buttonCss.styles}
      {counterCss.styles}
      {sliderCss.styles}
      {heartCss.styles}
      {dostsCss.styles}
    </div>
  );
}
